import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#111C16] text-white relative overflow-hidden" data-testid="footer">
      {/* Large background text */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none select-none overflow-hidden">
        <p className="font-display text-[12rem] md:text-[18rem] font-bold text-white/[0.02] leading-none tracking-tighter whitespace-nowrap -mb-16">
          Trademark Application Hub
        </p>
      </div>

      {/* Top CTA Banner */}
      <div className="relative z-10 border-b bg-[#1A3628] border-white/5" >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#C85A40] mb-3">Let's Work Together</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tighter leading-tight">
                Protect what<br />you've built.
              </h2>
            </div>
            <Link
              
              className="group flex items-center gap-4 border border-[#C85A40] rounded-full pl-8 pr-5 py-4 hover:bg-[#C85A40] hover:border-[#C85A40] transition-all duration-300 js-chat"
            >
              <span className="font-body text-sm font-medium text-white ">Register Your Trademark</span>
              <div className="w-10 h-10 rounded-full bg-white/10 border border-[#C85A40] group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <ArrowUpRight className="h-4 w-4 text-white" strokeWidth={2} />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* Left — Brand */}
          <div className="lg:col-span-4">
           <img src="/assets/img/brand/logo-light.png" alt="Trademark Application Hub" className="h-14" />
            <p className="font-body text-sm text-white/30 mt-4 mb-8 leading-relaxed max-w-sm">
              Safeguarding Innovation. Protecting Identity. Trusted by 10,000+ businesses with a 98% USPTO approval rate.
            </p>

            <div className="space-y-4">
              <a href="tel:+16466872501" className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#C85A40] transition-colors">
                  <Phone className="h-3.5 w-3.5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <span className="font-body text-sm text-white/50 group-hover:text-white transition-colors">+1 (213) 646 7133</span>
              </a>
              <a href="mailto:info@trademarkapplicationhub.com" className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#C85A40] transition-colors">
                  <Mail className="h-3.5 w-3.5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <span className="font-body text-sm text-white/50 group-hover:text-white transition-colors">info@trademarkapplicationhub.com</span>
              </a>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center">
                  <MapPin className="h-3.5 w-3.5 text-[#C85A40]" strokeWidth={1.5} />
                </div>
                <span className="font-body text-sm text-white/50">7080 Hollywood Blvd Suite 700, Los Angeles, CA 90028</span>
              </div>
            </div>
          </div>

          {/* Right — Links Grid */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Trademark */}
            <div>
              <h4 className="font-display text-[11px] font-bold text-white uppercase tracking-[0.2em] mb-5 pb-3 border-b border-white/10">Trademark</h4>
              <ul className="space-y-2.5">
                {[
                  ['Search & Registration', '/services/trademark/search-registration'],
                  ['Clearance Searches', '/services/trademark/clearance'],
                  ['Application Prep', '/services/trademark/application'],
                  ['Office Actions', '/services/trademark/office-actions'],
                  ['Monitoring', '/services/trademark/monitoring'],
                  ['Statement Of Use', '/services/trademark/statement-of-use'],
                  ['Ownership Change', '/services/trademark/ownership-change'],
                  ['Portfolio Mgmt', '/services/trademark/portfolio']
                ].map(([label, path]) => (
                  <li key={path}><Link to={path} className="font-body text-xs text-white/40 hover:text-[#C85A40] transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>

            {/* Copyright */}
            <div>
              <h4 className="font-display text-[11px] font-bold text-white uppercase tracking-[0.2em] mb-5 pb-3 border-b border-white/10">Copyright</h4>
              <ul className="space-y-2.5">
                {[
                  ['Registration', '/services/copyright/registration'],
                  ['Enforcement', '/services/copyright/enforcement'],
                  ['Assignments', '/services/copyright/assignments'],
                  ['Litigation', '/services/copyright/litigation'],
                  ['Portfolio Mgmt', '/services/copyright/portfolio'],
                  ['DMCA', '/services/copyright/dmca']
                ].map(([label, path]) => (
                  <li key={path}><Link to={path} className="font-body text-xs text-white/40 hover:text-[#C85A40] transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>

            {/* Patent */}
            <div>
              <h4 className="font-display text-[11px] font-bold text-white uppercase tracking-[0.2em] mb-5 pb-3 border-b border-white/10">Patent</h4>
              <ul className="space-y-2.5">
                {[
                  ['Patent Searches', '/services/patent/searches'],
                  ['Office Actions', '/services/patent/office-actions'],
                  ['Applications', '/services/patent/applications'],
                  ['Enforcement', '/services/patent/enforcement']
                ].map(([label, path]) => (
                  <li key={path}><Link to={path} className="font-body text-xs text-white/40 hover:text-[#C85A40] transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>

            {/* Company + Pages */}
            <div>
              <h4 className="font-display text-[11px] font-bold text-white uppercase tracking-[0.2em] mb-5 pb-3 border-b border-white/10">Company</h4>
              <ul className="space-y-2.5">
                {[
                  ['LLC Formation', '/services/company/llc'],
                  ['C Corporation', '/services/company/c-corp'],
                  ['S Corporation', '/services/company/s-corp'],
                  ['Non-Profit', '/services/company/non-profit']
                ].map(([label, path]) => (
                  <li key={path}><Link to={path} className="font-body text-xs text-white/40 hover:text-[#C85A40] transition-colors">{label}</Link></li>
                ))}
              </ul>

              <h4 className="font-display text-[11px] font-bold text-white uppercase tracking-[0.2em] mt-7 mb-4 pb-3 border-b border-white/10">Navigate</h4>
              <ul className="space-y-2.5">
                {[['Home', '/'], ['About', '/about'], ['Insights', '/blogs'], ['Contact', '/contact']].map(([label, path]) => (
                  <li key={path}><Link to={path} className="font-body text-xs text-white/40 hover:text-[#C85A40] transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between items-center gap-3">
          <a className="font-body text-[11px] text-white/50">&copy; 2026 Trademark Application Hub. All rights reserved.</a>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="font-body text-[11px] text-white/50 hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="font-body text-[11px] text-white/50 hover:text-white/60 transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
